var _a;
import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import { clamp } from '@polestar/component-warehouse-react/helpers/mathematical';
import { css } from '@emotion/react';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { createTransition } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { m as motion } from 'framer-motion';
import { isBrowser } from '@polestar/component-warehouse-react/helpers/supports';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import useDimensionsCallback from '@polestar/component-warehouse-react/hooks/useDimensions';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import useDirectionStyle from '@polestar/component-warehouse-react/hooks/useDirectionStyle';
import { minimalHitbox } from '@polestar/component-warehouse-react/helpers/style';
const defaultInteractive = !!(isBrowser && ((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: fine)').matches));
const ScrollIndicator = forwardRef(({ handleSize = 128, progress = 0, animated = true, orientation = Orientations.horizontal, scrollContentSize = 0, onProgress, interactive = defaultInteractive, hideTrack = false, hitAreaSize = 30, monochrome, ...props }, ref) => {
    const [dragging, setDragging] = useState(false);
    const [indicatorRef, dimensions] = useDimensionsCallback();
    const isHorizontal = orientation === Orientations.horizontal;
    const totalSize = dimensions ? (isHorizontal ? dimensions.width : dimensions.height) : 0;
    const { isRTL } = useDirection();
    const handleSizeCalculated = useMemo(() => (handleSize === 'auto' ? totalSize * (totalSize / scrollContentSize) || 0 : handleSize), [handleSize, totalSize, scrollContentSize]);
    const availableSize = totalSize - handleSizeCalculated;
    const percentPoint = availableSize / 100;
    const position = progress * percentPoint;
    const indicatorNode = useRef(null);
    const combinedRef = useCombinedRef(ref, indicatorNode, indicatorRef);
    const offsetRef = useRef(0);
    const handlePan = useCallback((event, info) => {
        if (!dimensions) {
            return;
        }
        const pixelPosition = Math.max(0, Math.min(availableSize, info.point[isHorizontal ? 'x' : 'y'] -
            offsetRef.current -
            dimensions[isHorizontal ? 'left' : 'top']));
        if (onProgress) {
            const newProgress = clamp((pixelPosition / availableSize) * 100, 0, 100);
            onProgress(isRTL ? 100 - newProgress : newProgress);
        }
    }, [availableSize, dimensions, isHorizontal, onProgress, isRTL]);
    const handlePanStart = useCallback((e) => {
        if (indicatorNode.current) {
            indicatorRef(indicatorNode.current);
        }
        // Firefox uses the non-standard property layerX/Y (https://developer.mozilla.org/en-US/docs/Web/API/UIEvent/layerX) which is deprecated in webkit (chrome/safari). Need to detect Firefox to ensure the correct calculations happen
        offsetRef.current =
            e[`offset${isHorizontal ? 'X' : 'Y'}`] +
                ((e === null || e === void 0 ? void 0 : e.originalTarget) !== undefined ? e[`layer${isHorizontal ? 'X' : 'Y'}`] || 0 : 0);
        setDragging(true);
    }, [indicatorRef, isHorizontal]);
    const handlePanEnd = useCallback(() => {
        setDragging(false);
    }, []);
    const handleClick = useCallback((e) => {
        if (onProgress) {
            const trackRect = e.currentTarget.getBoundingClientRect();
            const clientXPosition = ((e.clientX - trackRect.left) / totalSize) * 100;
            const clickProgress = isHorizontal
                ? isRTL
                    ? 100 - clientXPosition
                    : clientXPosition
                : ((e.clientY - trackRect.top) / totalSize) * 100;
            const nextPage = progress < clickProgress;
            const pageJump = (handleSizeCalculated / availableSize) * 100;
            const newProgress = clamp(nextPage ? progress + pageJump : progress - pageJump, 0, 100);
            onProgress(newProgress);
        }
    }, [progress, totalSize, handleSizeCalculated, isHorizontal, onProgress, availableSize, isRTL]);
    let handleProps = {};
    if (interactive && onProgress) {
        handleProps = {
            onPan: handlePan,
            onPanStart: handlePanStart,
            onPanEnd: handlePanEnd,
            dragging: dragging,
        };
    }
    return (React.createElement(Container, { key: isHorizontal ? 'h' : 'v', ...props, ref: combinedRef, horizontal: isHorizontal, hitAreaSize: hitAreaSize, ...aria({
            role: `scrollbar`,
            valuemin: 0,
            valuemax: 100,
            valuenow: progress,
            orientation,
        }) },
        interactive ? (React.createElement(Track, { onClick: handleClick, horizontal: isHorizontal, hidden: hideTrack, hitAreaSize: hitAreaSize })) : null,
        React.createElement(HandleHitArea, { initial: false, animate: {
                [isHorizontal ? 'translateX' : 'translateY']: `${useDirectionStyle(position) || 0}px`,
                [!isHorizontal ? 'width' : 'height']: `${hitAreaSize}px`,
                [isHorizontal ? 'width' : 'height']: `${handleSizeCalculated}px`,
            }, transition: createTransition({ disabled: dragging || !animated }), ...handleProps, interactive: interactive, horizontal: isHorizontal, hitAreaSize: hitAreaSize, monochrome: monochrome, "aria-hidden": "true" },
            React.createElement(Handle, { horizontal: isHorizontal, "aria-hidden": "true" }))));
});
const Container = styled.div(({ theme, hitAreaSize, horizontal }) => css(css `
        position: relative;
        user-select: none;
        overflow: hidden;

        &:before {
          content: '';
          position: absolute;
          background-color: ${theme.scrollIndicator.backgroundColor};
        }
      `, horizontal
    ? css `
            width: 100%;
            height: ${hitAreaSize}px;
            inset-block-start: 0;
            inset-inline-start: 0;

            &:before {
              inset-inline-start: 0;
              inset-inline-end: 0;
              inset-block-start: 50%;
              height: 2px;
              transform: translateY(-50%);
            }
          `
    : css `
            height: 100%;
            width: ${hitAreaSize}px;

            &:before {
              inset-block-start: 0;
              inset-block-end: 0;
              inset-inline-start: 50%;
              width: 2px;
              transform: translateX(-50%);
            }
          `));
const Track = styled.div((props) => css(css `
      position: absolute;
      inset-block-start: 0;
      inset-inline-start: 0;
      inset-inline-end: 0;
      inset-block-end: 0;
      visibility: ${props.hidden ? 'hidden' : 'visible'};

      &:hover {
        cursor: pointer;
      }
    `, minimalHitbox, props.horizontal
    ? css `
          height: ${props.hitAreaSize}px;
          inset-block-start: 50%;
          transform: translateY(-50%);
        `
    : css `
          position: absolute;
          width: ${props.hitAreaSize}px;
          inset-inline-start: 50%;
          transform: translateX(-50%);
        `));
const HandleHitArea = styled(motion.div)((props) => css(css `
      height: ${props.hitAreaSize}px;
      position: absolute;
      inset-inline-start: 0;
      inset-block-start: 0;
    `, props.interactive &&
    css `
        cursor: grab;

        > div {
          background: ${props.monochrome
        ? props.theme.scrollIndicator.monochromeHandleColor
        : props.theme.scrollIndicator.handleColor};
        }
      `, props.dragging &&
    css `
        cursor: grabbing;
      `, props.horizontal
    ? css `
          height: ${props.hitAreaSize}px;
        `
    : css `
          width: ${props.hitAreaSize}px;
        `));
const Handle = styled.div((props) => css(css `
      background: ${props.theme.scrollIndicator.handleColor};
      position: absolute;
      outline: none;
    `, props.horizontal
    ? css `
          height: 2px;
          inset-inline-start: 0;
          inset-block-start: 50%;
          width: 100%;
          transform: translateY(-50%);
        `
    : css `
          width: 2px;
          inset-inline-start: 50%;
          inset-block-start: 0;
          height: 100%;
          transform: translateX(-50%);
        `));
ScrollIndicator.displayName = 'ScrollIndicator';
export default ScrollIndicator;
